import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { fallbackBlogPosts } from '../prerenderData';
import './BlogPostPage.css';
import { apiFetch } from '../services/api';

// Extract H2/H3 headings from HTML content for table of contents
function extractHeadings(html) {
  if (!html) return [];
  const regex = /<h([23])[^>]*>(.*?)<\/h[23]>/gi;
  const headings = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    const text = match[2].replace(/<[^>]*>/g, '').trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    headings.push({ level: parseInt(match[1]), text, id });
  }
  return headings;
}

// Inject id attributes into heading tags in the HTML
function injectHeadingIds(html) {
  if (!html) return html;
  return html.replace(/<h([23])([^>]*)>(.*?)<\/h[23]>/gi, (match, level, attrs, inner) => {
    const text = inner.replace(/<[^>]*>/g, '').trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `<h${level}${attrs} id="${id}">${inner}</h${level}>`;
  });
}

function BlogPostPage() {
  const { slug } = useParams();

  // Start from the build-time snapshot (the data the pre-rendered HTML was
  // made from) so hydration adopts that DOM; the live post replaces it as
  // soon as the fetch below resolves.
  const prerenderPost = fallbackBlogPosts.find(p => p.slug === slug) || null;

  const [post, setPost] = useState(prerenderPost);
  const [loading, setLoading] = useState(!prerenderPost);

  useEffect(() => {
    // Always fetch live data (replaces fallback on hydration)
    apiFetch(`/api/blog/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => {
        setPost(data);
        setLoading(false);
      })
      .catch(() => { if (!prerenderPost) setLoading(false); });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (d) => new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return <div className="bpp-loading"><div className="bpp-spinner"></div></div>;
  if (!post) return (
    <div className="bpp-notfound">
      <h2>Post not found</h2>
      <p>The article you're looking for doesn't exist or has been removed.</p>
      <Link to="/blog">← Back to Blog</Link>
    </div>
  );

  // CQ-18: prefer snake_case author fields, fall back to the deprecated
  // camelCase variants until the migration script renames the data.
  const authorName = post.author_name || post.authorName;
  const authorBio = post.author_bio || post.authorBio;
  const authorCredentials = post.author_credentials || post.authorCredentials;

  return (
    <div className="bpp-page">
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || `${post.title} - ASG Shading blog`}
        keywords={post.tags ? post.tags.join(', ') : undefined}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.featured_image}
        dateModified={post.updatedAt || post.createdAt}
        jsonLd={[SEO.articleSchema(post), SEO.breadcrumbSchema([{ name: 'Blog', url: '/blog' }, { name: post.title }]), SEO.webPageSchema({ type: 'WebPage', name: post.title, description: post.seo_description || post.excerpt, url: `/blog/${post.slug}` })]}
      />
      <Breadcrumbs items={[{ name: 'Blog', url: '/blog' }, { name: post.title }]} />
      <article className="bpp-article">
        {/* Header */}
        <header className="bpp-header">
          <Link to="/blog" className="bpp-back">← Blog</Link>
          {post.tags && post.tags.length > 0 && (
            <div className="bpp-tags">
              {post.tags.map((tag, i) => <span className="bpp-tag" key={i}>{tag}</span>)}
            </div>
          )}
          <h1 className="bpp-title">{post.title}</h1>
          {post.excerpt && <p className="bpp-excerpt">{post.excerpt}</p>}
          <div className="bpp-meta">
            {authorName && <span className="bpp-author">By {authorName}</span>}
            {authorName && <span className="bpp-meta-sep">·</span>}
            <time>{formatDate(post.published_at || post.createdAt)}</time>
          </div>
        </header>

        {/* Featured image */}
        {post.featured_image && (
          <div className="bpp-featured-img">
            <img src={post.featured_image} alt={post.title} width={1200} height={675} loading="eager" />
          </div>
        )}

        {/* Table of contents */}
        {post.content && extractHeadings(post.content).length >= 3 && (
          <nav className="bpp-toc">
            <p className="bpp-toc-title">In this article</p>
            <ul className="bpp-toc-list">
              {extractHeadings(post.content).map((h, i) => (
                <li key={i} className={h.level === 3 ? 'bpp-toc-sub' : ''}>
                  <a href={`#${h.id}`}>{h.text}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Content */}
        <div className="bpp-content" dangerouslySetInnerHTML={{ __html: post.content ? injectHeadingIds(post.content) : '' }} />

        {/* Tags — repeated at the bottom under a "Filed under" label so
            readers reaching the end can pivot to related topics. */}
        {post.tags && post.tags.length > 0 && (
          <div className="bpp-tags-bottom">
            <p className="bpp-tags-bottom-label">Filed under</p>
            <div className="bpp-tags bpp-tags--bottom">
              {post.tags.map((tag, i) => (
                <Link key={i} to={`/blog?tag=${encodeURIComponent(tag)}`} className="bpp-tag">
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Author box */}
        {authorName && (
          <div className="bpp-author-box">
            <div className="bpp-author-box-inner">
              <div className="bpp-author-box-icon">
                <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/></svg>
              </div>
              <div>
                <p className="bpp-author-box-name">{authorName}</p>
                {authorCredentials && <p className="bpp-author-box-creds">{authorCredentials}</p>}
                {authorBio && <p className="bpp-author-box-bio">{authorBio}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="bpp-footer">
          <div className="bpp-share">
            <span>Share this article</span>
          </div>
          <Link to="/blog" className="bpp-back-bottom">← Back to Blog</Link>
        </footer>
      </article>
    </div>
  );
}

export default BlogPostPage;
